// src/app/pages/checkout/checkout.component.ts
import { Component, OnInit, ViewChild, ViewChildren, QueryList, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { TermsComponent } from '../../components/terms/terms.component';
import { Config, FieldConfig, Plan, Integration } from '../../models/config.model';
import { TaxParams } from '../../models/tax.model';
import { TaxService } from 'src/app/services/tax.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { AuthService } from 'src/app/services/auth.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { WebButtonComponent } from '../../components/web-button/web-button.component';
import { WebFormFieldComponent } from '../../components/web-form-field/web-form-field.component';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss']
})
export class CheckoutComponent implements OnInit, OnDestroy {
  @ViewChild('termsModal') termsModalComponent: TermsComponent;
  @ViewChildren(WebFormFieldComponent) formFields: QueryList<WebFormFieldComponent>;
  @ViewChild('submitButton') submitButton: WebButtonComponent;

  config: Config;
  selected: Plan;
  creditCardInfoStripe: any;
  testUsageRangeIndex: number = 0;
  estimatedTax: number | null = null;
  taxKey: string;
  taxMessage: string = '';
  infoFormGroup: FormGroup;
  termsError: boolean = false;

  currentSection = 0;
  slideDirection = '';
  delayFocusBy = 350;
  formInUse: any;

  integrationEnabled: boolean = false;
  selectedIntegration: Integration | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private taxService: TaxService,
    private sharedDataService: SharedDataService,
    private authService: AuthService,
    private navigationService: NavigationService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.sharedDataService.getConfig()
      .pipe(filter(config => config !== null))
      .subscribe((config) => {
        this.config = config;
        const savedSection = this.localStorageService.getItem('currentSection');
        this.currentSection = savedSection ? parseInt(savedSection, 10) : 0;
        this.initializeForm();

        if (!this.authService.isExistingCustomerMode()) {
          this.sharedDataService.getPlan().subscribe((plan) => {
            this.selected = plan;
          });
          this.sharedDataService.getRangeIndex().subscribe((index) => {
            this.testUsageRangeIndex = index;
          });
        } else {
          this.creditCardInfoStripe = this.sharedDataService.getCreditCardInfoStripe();
        }

        this.loadAndTrackData();
        this.focusOnNextActionableElement();
    }));

    this.subscriptions.add(this.sharedDataService.getIntegrationEnabled().subscribe(enabled => {
      this.integrationEnabled = enabled;
      if (!enabled) {
        this.selectedIntegration = null;
      }
      this.computeTax();

    }));

    this.subscriptions.add(this.sharedDataService.getSelectedIntegration().subscribe(integration => {
      this.selectedIntegration = integration;
      this.computeTax();

    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Compute the plan price based on the selected usage range.
   * If the plan allows integration and the integration is enabled and selected,
   * its static fee is added to the base price.
   */
  get selectedPrice(): number | null {
    if (
      this.planPrice
    ) {
      let totalPrice = this.planPrice;
      totalPrice += this.integrationPrice;

      return totalPrice;
    }
    return null;
  }

  get planPrice(): number | null {
    if (
      this.selected &&
      this.selected.priceRanges &&
      this.selected.priceRanges.length > this.testUsageRangeIndex
    ) {
      const priceMultiplier = this.selected.priceMultiplier || 1;
      const priceRange = this.selected.priceRanges[this.testUsageRangeIndex];
      let { max, price } = priceRange;


      let planPrice = price * priceMultiplier;

      let perUnit = !!this.selected.perUnit;

      if(perUnit) {
        planPrice = planPrice * max;
      }

      return planPrice;

    }
    return null;
  }

  get integrationPrice(): number {
    if (
      this.selected.allowIntegration &&
      this.integrationEnabled &&
      this.selectedIntegration
    ) {
      return this.selectedIntegration.price;
    } else{
      return 0;
    }
  }

  get totalPrice(): number {
    return this.selectedPrice + this.estimatedTax;
  }

  // Use selectedPrice as the subTotal unless in existing customer mode
  get price(): number {
    if (this.creditCardInfoStripe) {
      return parseFloat(this.creditCardInfoStripe.subTotal);
    } else {
      return this.selectedPrice;
    }
  }

  get isTaxExempt(): boolean {
    const isExempt = this.creditCardInfoStripe?.isTaxExempt;
    return isExempt === true || isExempt === 'true';
  }

  get estimatedUsage(): string {
    const selectedRange = this.selected.priceRanges[this.testUsageRangeIndex];
    return `${selectedRange.min}-${selectedRange.max} Tests`;
  }

  // SFX-25902: the summary's billing cycle should reflect the term chosen via the
  // entry-point URL (?term=monthly|annual), falling back to the plan's static
  // period when no term was supplied (legacy plan-selection flow).
  get billingCycle(): string {
    return this.sharedDataService.getBillingTerm() || this.selected?.period || '';
  }

  get isDirectEntry(): boolean {
    return this.sharedDataService.isDirectEntry();
  }

  get formControlKeys() {
    return Object.keys(this.infoFormGroup.controls);
  }

  get isSubscriptionAccount() {
    return (
      this.creditCardInfoStripe['ecommerceTransactionType'].indexOf(
        'TermBasedBill'
      ) !== -1
    );
  }

  get uiLabels() {
    return this.formInUse.uiLabels;
  }
  get isLastSection(): boolean {
    return this.currentSection === this.formInUse.formSections.length - 1;
  }

  private initializeForm(): void {
    const group: any = {};
    const formName = this.authService.isExistingCustomerMode() ? 'existingAccountCardInfoForm' : 'customerInfoForm';
    this.formInUse = this.config[formName];

    this.formInUse.formSections.forEach((section: FieldConfig[]) => {
      section.forEach((input: FieldConfig) => {
        if (input.isSeparator) {
          return;
        }
        let initialValue = '';
        if (input.options && input.options.length > 0) {
          initialValue = input.options[0][0];
        }
        group[input.id] = new FormControl(initialValue);
      });
    });

    const savedTermsAccepted = this.localStorageService.getItem('hasAcceptedTermsOfService');
    group['termsAccepted'] = new FormControl(savedTermsAccepted !== null ? savedTermsAccepted : false, Validators.requiredTrue);
    this.infoFormGroup = this.formBuilder.group(group);

    // Subscribe to changes on the termsAccepted control to save any updates
    this.infoFormGroup.get('termsAccepted').valueChanges.subscribe(val => {
      this.localStorageService.setItem('hasAcceptedTermsOfService', val);
    });
  }

  private loadAndTrackData(): void {
    const formInfo = this.sharedDataService.getFormInfo();
    if (formInfo) {
      // Remove the termsAccepted state to preserve the separately stored value
      delete formInfo.termsAccepted;
      this.infoFormGroup.patchValue(formInfo);
    }

    if (this.creditCardInfoStripe) {
      this.infoFormGroup.patchValue(this.mapAndCleanCreditCardInfoForForm());
    }

    this.computeTax();

    // Update the shared form info whenever the form changes
    this.infoFormGroup.valueChanges.subscribe((formData) => {
      this.sharedDataService.setFormInfo(formData);
    });
  }


  getControlAsFormControl(controlName: string): FormControl {
    const control = this.infoFormGroup.get(controlName);
    if (!control) {
      throw new Error(`Control with name '${controlName}' not found`);
    }
    return control as FormControl;
  }

  shouldDisplay(inputId: string): boolean {
    let input: FieldConfig;
    for (const section of this.formInUse.formSections) {
      const foundInput = section.find((i) => i.id === inputId);
      if (foundInput) {
        input = foundInput;
        break;
      }
    }
    if (!input || !input.conditionalDisplay) return true;
    const control = this.getControlAsFormControl(input.conditionalDisplay.dependsOn);
    const displayOnValue = input.conditionalDisplay.displayOnValue;
    const retVal = control.value === displayOnValue;
    if (!retVal) {
      this.getControlAsFormControl(input.id).setValue('');
      this.getControlAsFormControl(input.id).disable();
    } else {
      this.getControlAsFormControl(input.id).enable();
    }
    return retVal;
  }

  handleChangeEvent(changeAction: string): void {
    if (changeAction === 'computeTax') {
      this.computeTax();
    }
  }

  handleKeyDown(event: KeyboardEvent, input: HTMLInputElement | FieldConfig) {
    if (event.key === 'Tab') {
      const currentSectionInputs = this.formInUse.formSections[this.currentSection];
      const firstInputId = currentSectionInputs[0].id;
      const lastInputId = currentSectionInputs[currentSectionInputs.length - 1].id;
      const isFirstInput = input.id === firstInputId;
      const isLastInput =
        (this.isLastSection &&
          input instanceof HTMLInputElement &&
          input.id === 'terms') ||
        (!this.isLastSection && input.id === lastInputId);
      if (event.shiftKey) {
        if (isFirstInput) {
          event.preventDefault();
          if (this.isLastSection) {
            this.slideDirection = 'leftToRight';
            this.currentSection--;
            setTimeout(() => {
              let lastActionableInput = this.getLastActionableInput();
              if (lastActionableInput) {
                lastActionableInput.focus();
              }
            }, 200);
          } else {
            const lastActionableInput = this.getLastActionableInput();
            if (lastActionableInput) {
              event.preventDefault();
              lastActionableInput.focus();
            }
          }
        }
      } else {
        if (isLastInput) {
          event.preventDefault();
          if (this.isSectionValid()) {
            this.onSubmit();
          } else {
            this.focusOnNextActionableElement(false);
          }
        }
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.isSectionValid()) {
        this.onSubmit();
      } else {
        this.focusOnNextActionableElement();
      }
    }
  }

  private getLastActionableInput(): HTMLInputElement | null {
    const currentSectionInputs = this.formInUse.formSections[this.currentSection];
    for (let i = currentSectionInputs.length - 1; i >= 0; i--) {
      const input = currentSectionInputs[i];
      if (input.isSeparator) continue;
      const control = this.getControlAsFormControl(input.id);
      if (!control.disabled) {
        const fieldComponent = this.formFields?.find((field) => field.id === input.id);
        if (fieldComponent) {
          return fieldComponent.getInputElement();
        }
      }
    }
    return null;
  }

  scrollToCurrentSection(): void {
    const formSectionsContainer = document.querySelector('.form-sections-container');
    if (formSectionsContainer) {
      formSectionsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  focusOnNextActionableElement(delayFocus = true): void {
    let currentSection = this.currentSection;
    let elementToFocus;
    while (currentSection < this.formInUse.formSections.length) {
      const currentSectionInputs = this.formInUse.formSections[currentSection];
      for (const input of currentSectionInputs) {
        if (input.isSeparator) continue;
        const control = this.getControlAsFormControl(input.id);
        if (control.invalid || control.value === '') {
          const fieldComponent = this.formFields?.find((field) => field.id === input.id);
          if (fieldComponent) {
            elementToFocus = fieldComponent;
            break;
          }
        }
      }
      if (!elementToFocus && this.currentSection === this.formInUse.formSections.length - 1) {
        const termsAcceptedControl = this.infoFormGroup.get('termsAccepted');
        if (termsAcceptedControl && termsAcceptedControl.invalid) {
          elementToFocus = document.getElementById('terms') as HTMLInputElement;
          break;
        }
      }
      currentSection++;
    }
    this.localStorageService.setItem('currentSection', this.currentSection.toString());
    const delay = delayFocus ? this.delayFocusBy : 0;
    this.scrollToCurrentSection();
    setTimeout(() => {
      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        elementToFocus.focus();
      } else if (this.submitButton) {
        this.submitButton.focus();
      }
    }, delay);
  }

  computeTax(): void {
    if (this.isTaxExempt) {
      return;
    }
    const vals = this.infoFormGroup.value;
    const cardholderCity = vals.city;
    const cardholderState =
      vals.state || vals.mexicoProvince || vals.canadaProvince || null;
    const cardholderZip = vals.zip;
    const cardholderCountry = vals.country;
    if (
      (cardholderCountry === 'US' && cardholderCity && cardholderState) ||
      cardholderZip
    ) {
      const taxDetails: TaxParams = {
        cardholderCity: cardholderCity,
        cardholderState: cardholderState,
        cardholderCountry: cardholderCountry,
        cardholderZip: cardholderZip,
        price: '' + this.price,
      };
      const taxKey = JSON.stringify(taxDetails);
      if (this.taxKey === taxKey) {
        return;
      }
      this.taxKey = taxKey;
      this.taxService.computeTax(taxDetails).subscribe({
        next: (result: any) => {
          if (result.result) {
            this.estimatedTax = parseFloat(result.result.TotalTax || 0);
          } else if (result.results) {
            let strError = result.results.replace('Internal Server Error:', '');
            strError = strError.replace(/\s+/, '');
            const objJSON = JSON.parse(strError);
            this.taxMessage =
              objJSON.Messages?.[0]?.Summary ?? 'Error calculating tax';
            this.estimatedTax = null;
          }
        },
      });
    } else {
      this.estimatedTax = null;
    }
  }

  isSectionValid(): boolean {
    const currentSectionInputs = this.formInUse.formSections[this.currentSection];
    for (const input of currentSectionInputs) {
      if (input.isSeparator) continue;
      const control = this.getControlAsFormControl(input.id);
      if (control.invalid) {
        return false;
      }
    }
    if (this.currentSection === this.formInUse.formSections.length - 1) {
      const termsAcceptedControl = this.infoFormGroup.get('termsAccepted');
      if (termsAcceptedControl && termsAcceptedControl.invalid) {
        return false;
      }
    }
    return true;
  }

  onSubmit(): void {
    if (this.currentSection === this.formInUse.formSections.length - 1) {
      if (this.infoFormGroup.valid && this.isSectionValid()) {
        this.sharedDataService.setFormInfo(this.infoFormGroup.value);
        this.navigationService.navigateForward();
      } else {
        Object.values(this.infoFormGroup.controls).forEach((control) => {
          if (control.invalid) {
            control.markAsTouched();
          }
        });
        this.focusOnNextActionableElement();
      }
    } else {
      this.slideDirection = 'rightToLeft';
      this.currentSection++;
      this.localStorageService.setItem('currentSection', this.currentSection.toString());
      setTimeout(() => {
        this.focusOnNextActionableElement(false);
      }, this.delayFocusBy);
    }
  }

  onBack(): void {
    if (this.currentSection > 0) {
      this.slideDirection = 'leftToRight';
      this.currentSection--;
      this.localStorageService.setItem('currentSection', this.currentSection.toString());
      setTimeout(() => this.focusOnNextActionableElement(false), 200);
    } else {
      this.navigationService.navigateBack();
    }
  }

  acceptedTerms(accepted: boolean): void {
    // Called by the TermsComponent when the user accepts or rejects.
    this.infoFormGroup.patchValue({ termsAccepted: accepted });
  }

  openTermsModal(event: MouseEvent): void {
    // Delegate to the TermsComponent's toggleModal method.
    this.termsModalComponent.toggleModal(event.clientY);
  }

  getSubmitButtonLabel(): string {
    return this.currentSection === this.formInUse.formSections.length - 1
      ? this.formInUse.uiLabels.submitBtn
      : 'Next';
  }

  mapAndCleanCreditCardInfoForForm(): any {
    let cc = this.creditCardInfoStripe;
    if (!cc.contactName) {
      return {};
    }
    if (cc.contactCountry?.length !== 2) {
      cc.contactCountry = 'US';
    }
    if (cc.contactState?.length !== 2) {
      cc.contactState = '';
    }
    if (cc.contactZip?.length < 2) {
      cc.contactZip = '';
    }
    const retVal = {
      cardholderName: cc.contactName,
      title: '',
      email: cc.contactEmail || '',
      phone: cc.contactPhone || '',
      address1: cc.contactAddress1 || '',
      address2: cc.contactAddress2 || '',
      city: cc.contactCity || '',
      state: cc.contactState || '',
      zip: cc.contactZip || '',
      country: cc.contactCountry || '',
      reference: '',
      termsAccepted: false,
    };
    delete cc.contactName;
    delete cc.contactEmail;
    delete cc.contactPhone;
    delete cc.contactAddress1;
    delete cc.contactAddress2;
    delete cc.contactCity;
    delete cc.contactState;
    delete cc.contactZip;
    delete cc.contactCountry;
    this.sharedDataService.setCreditCardInfoStripe(cc);
    return retVal;
  }

  private updatePriceDisplay(): void {
    // Angular change detection handles UI updates automatically.
  }
}
